import React, { useRef, useEffect, useState, ForwardedRef, forwardRef } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import clxs from 'classnames';
import FormSection from '../../../Layout/FormSection';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import StartIcon from '@mui/icons-material/PlayCircleFilled';

interface VideoPreviewProps {
  videoURL?: string;
  onConfirm?: () => void;
  videoContainerStyle?: string;
  videoStyle?: string;
  attributes?: any;
  isVideoStoppable?: boolean;
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    videoContainer: {
      height: '80vh',
      display: 'flex',
      marginTop: theme.spacing(8),
      width: '100%',
      position: 'relative',
      [theme.breakpoints.down('md')]: {
        height: '70vh',
      },
    },
    videoPreview: {
      backgroundColor: 'black',
      flexGrow: 1,
      overflow: 'hidden',
      objectFit: 'contain',
    },
    videoStop: {
      opacity: 0.5,
    },
    iconButton: {
      fontSize: 80,
    },
    iconContainerCentered: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      color: '#EEE',
    },
  }),
  { name: 'VideoPreview' },
);

const VideoPreview = (props: VideoPreviewProps, ref: ForwardedRef<any>) => {
  const { videoURL, onConfirm, videoContainerStyle = '', videoStyle = '', attributes = {}, isVideoStoppable = true } = props;
  const classes = useStyles();
  const videoElement = useRef<HTMLVideoElement>(null);
  const [showPlayButton, setShowPlayButton] = useState<boolean>(!attributes.autoPlay);
  useEffect(() => {
    if (!ref && videoElement && videoElement.current) {
      const element = videoElement.current;
      const listenEnded = () => {
        element.currentTime = 0;
        element.pause();
        setShowPlayButton(true);
      };
      const listenPlay = () => {
        setShowPlayButton(false);
      };
      const listenPause = () => {
        setShowPlayButton(true);
      };
      element.addEventListener('ended', listenEnded);
      element.addEventListener('play', listenPlay);
      element.addEventListener('pause', listenPause);
      return () => {
        element.removeEventListener('ended', listenEnded);
        element.removeEventListener('play', listenPlay);
        element.removeEventListener('pause', listenPause);
      };
    }
  }, [ref, videoElement, setShowPlayButton]);
  const video = (
    <Stack spacing={4} alignItems="center">
      <div className={videoContainerStyle ? videoContainerStyle : classes.videoContainer}>
        <video
          className={clxs(videoStyle ? videoStyle : classes.videoPreview, showPlayButton && isVideoStoppable && classes.videoStop)}
          ref={ref || videoElement}
          playsInline
          onClick={(ev: any) => {
            if (!isVideoStoppable) {
              return;
            }
            if (ev.target.paused) {
              ev.target.currentTime = 0;
              ev.target.play();
            } else {
              ev.target.currentTime = 0;
              ev.target.pause();
            }
          }}
          {...attributes}
        >
          <source src={videoURL} type="video/mp4" />
        </video>
        {!ref && showPlayButton && (
          <>
            <div className={classes.iconContainerCentered}>
              <IconButton
                className={classes.iconButton}
                onClick={() => {
                  if (videoElement && videoElement.current) {
                    videoElement.current.currentTime = 0;
                    videoElement.current.play();
                  }
                }}
                size="large"
              >
                <StartIcon fontSize="inherit" htmlColor="white" />
              </IconButton>
            </div>
          </>
        )}
      </div>
      {onConfirm && (
        <FormSection sx={{ paddingBottom: 4 }}>
          <Button fullWidth variant="outlined" size="large" onClick={onConfirm}>
            Confirm Video
          </Button>
        </FormSection>
      )}
    </Stack>
  );
  return onConfirm ? <DialogContent>{video}</DialogContent> : video;
};

export default forwardRef<any, VideoPreviewProps>(VideoPreview);
